import type { NuevaVersionPlantilla, PlantillaDocumento, ServicioCatalogo, VariablePlantilla } from '../../../api/ops/servicios';

/**
 * Los datos que Citas sabe rellenar (G484, `Consentimientos::VARIABLES`): sin `cliente.telefono` y con `cita.codigo`. Una
 * plantilla con otra variable obligatoria no se puede asignar a un servicio. El ejemplo es el de la vista previa.
 */
export const VARIABLES: readonly { nombre: string; texto: string; ejemplo: string; tipo: VariablePlantilla['type'] }[] = [
    { nombre: 'cliente.nombre', texto: 'Nombre del cliente', ejemplo: 'Ana Martín', tipo: 'text' },
    { nombre: 'cita.codigo', texto: 'Código de la cita', ejemplo: 'LOLA-7Q4K', tipo: 'text' },
    { nombre: 'servicio.nombre', texto: 'Servicio', ejemplo: 'Tinte', tipo: 'text' },
    { nombre: 'cita.fecha', texto: 'Fecha de la cita', ejemplo: 'sábado 17 de octubre', tipo: 'date' },
    { nombre: 'cita.hora', texto: 'Hora', ejemplo: '10:00', tipo: 'time' },
    { nombre: 'profesional.nombre', texto: 'Profesional', ejemplo: 'Elena Soto', tipo: 'text' },
    { nombre: 'negocio.nombre', texto: 'Tu negocio', ejemplo: 'Peluquería Lola', tipo: 'text' },
];

/** «Lo firmado vale»: días (vacío, solo para esa cita). */
export const VALIDEZ: readonly { valor: string; texto: string }[] = [
    { valor: '', texto: 'Solo para esa cita' },
    { valor: '30', texto: '30 días' },
    { valor: '180', texto: '6 meses' },
    { valor: '365', texto: '1 año' },
    { valor: '730', texto: '2 años' },
];

export const textoValidez = (dias: number | null | undefined): string => VALIDEZ.find((v) => v.valor === (dias == null ? '' : String(dias)))?.texto ?? `${String(dias)} días`;

export type PestanaPlantilla = 'texto' | 'vista' | 'servicios' | 'versiones';

export const esPestanaPlantilla = (x: string | null): x is PestanaPlantilla => x === 'texto' || x === 'vista' || x === 'servicios' || x === 'versiones';

export interface FormPlantilla {
    nombre: string;
    titulo: string;
    texto: string;
    nivel: 'advanced' | 'qualified';
    identidad: boolean;
    vale: string;
}

export function formVacio(): FormPlantilla {
    return { nombre: '', titulo: '', texto: 'Yo, {{cliente.nombre}}, acepto que {{negocio.nombre}} me aplique el servicio «{{servicio.nombre}}» el {{cita.fecha}}.\n\n', nivel: 'advanced', identidad: true, vale: '' };
}

export function formDe(t: PlantillaDocumento): FormPlantilla {
    const v = t.current_version;
    return { nombre: t.name, titulo: v.title, texto: v.body, nivel: v.signature_level, identidad: v.identity_check, vale: v.validity_days == null ? '' : String(v.validity_days) };
}

/** Las variables que usa el texto (`{{x}}`), en el orden en que salen. */
export function variablesDe(texto: string): string[] {
    const vistas: string[] = [];
    for (const m of texto.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
        const v = (m[1] ?? '').trim();
        if (!vistas.includes(v)) {
            vistas.push(v);
        }
    }
    return vistas;
}

export interface ErroresPlantilla {
    nombre?: string;
    titulo?: string;
    texto?: string;
}

export function erroresPlantilla(f: FormPlantilla): ErroresPlantilla {
    const e: ErroresPlantilla = {};
    if (f.nombre.trim() === '') {
        e.nombre = 'Ponle un nombre.';
    }
    if (f.titulo.trim() === '') {
        e.titulo = 'Escribe el título.';
    }
    const malas = variablesDe(f.texto).filter((v) => !VARIABLES.some((x) => x.nombre === v));
    if (f.texto.trim() === '') {
        e.texto = 'Escribe el texto.';
    } else if (malas.length > 0) {
        e.texto = `«${malas[0] ?? ''}» no es un dato que se pueda rellenar.`;
    }
    return e;
}

/** La versión que se publica, con las variables que usa el texto declaradas (obligatorias, G484). */
export function versionDe(f: FormPlantilla): NuevaVersionPlantilla {
    const variables: VariablePlantilla[] = variablesDe(f.texto).flatMap((n) => {
        const v = VARIABLES.find((x) => x.nombre === n);
        return v === undefined ? [] : [{ name: n, type: v.tipo, required: true, label: v.texto }];
    });
    return { title: f.titulo.trim(), body: f.texto, variables, signature_level: f.nivel, identity_check: f.identidad, validity_days: f.vale === '' ? null : Number(f.vale) };
}

/** El texto con los datos de ejemplo (lo que no se puede rellenar se queda tal cual y se dice). */
export function vistaPrevia(texto: string): { texto: string; malas: string[] } {
    const malas: string[] = [];
    const relleno = texto.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (m: string, k: string) => {
        const v = VARIABLES.find((x) => x.nombre === k.trim());
        if (v === undefined) {
            malas.push(k.trim());
            return m;
        }
        return v.ejemplo;
    });
    return { texto: relleno, malas };
}

/** Los valores de ejemplo para `previewDocumentTemplate`. */
export const EJEMPLOS: Readonly<Record<string, string>> = Object.fromEntries(VARIABLES.map((v) => [v.nombre, v.ejemplo]));

export const lineaFirma = (f: FormPlantilla): string => `Firma ${f.nivel === 'qualified' ? 'cualificada' : 'avanzada'}${f.identidad ? ', con su identidad verificada' : ''} · ${f.vale === '' ? 'solo para esa cita' : `vale ${textoValidez(Number(f.vale)).toLocaleLowerCase('es')}`}`;

/** Los servicios activos que piden la plantilla. */
export const serviciosQueLaPiden = (t: PlantillaDocumento, servicios: readonly ServicioCatalogo[]): ServicioCatalogo[] => servicios.filter((s) => s.status === 'active' && s.current_version.consent_template_id === t.id);
